import { useCallback } from 'react';
import { useGetProjectCommand } from '@atlassian/jira-command-palette-common/src/common/ui/command/project/main.tsx';
import type { Command } from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { PROJECT_LIMIT } from '../constants.tsx';
import { messages } from '../messages.tsx';
import type { GetQuickSearchChildResultProps } from '../types.tsx';
import {
	getQuickSearchProjectScope,
	transformQuickSearchProjectResultToProject,
} from '../utils.tsx';
import { useGetViewAllCommand } from '../view-all/index.tsx';
import { ViewAllType, getViewAllUrl } from '../view-all/utils.tsx';

type UseGetQuickSearchProjectCommands = {
	section: string;
	showViewAll: boolean;
};

export const useGetQuickSearchProjectCommands = ({
	section,
	showViewAll,
}: UseGetQuickSearchProjectCommands) => {
	const { formatMessage } = useIntl();

	const { getProjectCommand } = useGetProjectCommand();
	const { getViewAllCommand } = useGetViewAllCommand();

	const getQuickSearchProjectCommands = useCallback(
		({ search, quickSearchResponseData, error }: GetQuickSearchChildResultProps): Command[] => {
			const projectScope = getQuickSearchProjectScope(quickSearchResponseData);

			// No results
			if (!projectScope || error || projectScope?.error || projectScope?.results?.length === 0) {
				return [];
			}
			// There are results!
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const commands: Command[] = projectScope.results
				.slice(0, PROJECT_LIMIT)
				.map((project) =>
					getProjectCommand({
						project: transformQuickSearchProjectResultToProject(project),
						keywords: search,
						section,
						analytics: {
							isSearchResult: true,
						},
					}),
				)
				.filter((project) => project) as Command[];

			if (!showViewAll || projectScope.results.length <= PROJECT_LIMIT) {
				return commands;
			}

			const viewAllCommand = getViewAllCommand({
				id: 'view-all-projects',
				keywords: search,
				section,
				name: formatMessage(messages.viewAllResults),
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				url: getViewAllUrl(ViewAllType.PROJECT, search)!,
				analyticsPayload: { action: 'viewAllProjects', size: projectScope.size },
			});

			return commands.concat([viewAllCommand]);
		},
		[formatMessage, getProjectCommand, getViewAllCommand, section, showViewAll],
	);

	return { getQuickSearchProjectCommands };
};
